import React, { useState, useEffect } from 'react';
import { Menu, X, ChevronDown } from 'lucide-react';

const useResponsive = () => {
  const [screenWidth, setScreenWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1200
  );

  useEffect(() => {
    const handleResize = () => setScreenWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return {
    screenWidth,
    isMobile: screenWidth < 640,
    isTablet: screenWidth >= 640 && screenWidth < 1024,
    isDesktop: screenWidth >= 1024,
    isSmall: screenWidth < 375
  };
};

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isShopOpen, setIsShopOpen] = useState(false);
  const [isMusicOpen, setIsMusicOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const { isMobile, isTablet, isDesktop } = useResponsive();

  const SHOPIFY_DOMAIN = '1e0297-a4.myshopify.com';
  const SHOPIFY_STOREFRONT_TOKEN = '77c7860ecca2f00853d68ec0cfb67558';
  const SHOPIFY_API_VERSION = '2024-07';

  const fetchCategories = async () => {
    try {
      const query = `
        {
          collections(first: 20) {
            edges {
              node {
                id
                title
                handle
              }
            }
          }
        }
      `;
      const res = await fetch(
        `https://${SHOPIFY_DOMAIN}/api/${SHOPIFY_API_VERSION}/graphql.json`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': SHOPIFY_STOREFRONT_TOKEN
          },
          body: JSON.stringify({ query })
        }
      );
      const json = await res.json();
      setCategories(json.data.collections.edges.map(e => e.node));
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const navItems = [
    { name: 'HOME', href: '/' },
    { name: 'SHOP', dropdown: true },
    { name: 'DROPS', href: 'https://1e0297-a4.myshopify.com/products/superhung-vest-red-legend?variant=55231979585861' },
    { name: 'HNH', href: 'https://hnhmess.com/' },
    { name: 'EARN', href: '#' },
    { name: 'LEGENDBOARD', href: '#' },
    { name: 'TELEGRAM', href: '#' },
    { name: 'MUSIC', dropdown: true, musicDropdown: true }
  ];

  const musicOptions = [
    { name: 'Listen Music', href: '#hotmess-banner' },
    { name: 'Listen Radio', href: 'https://hotmess-radio.websiteradio.co/' }
  ];

  const isExternalLink = (url) =>
    /^https?:\/\//i.test(url) && !url.startsWith(window.location.origin);

const handleLinkClick = (href, e) => {
  if (href.startsWith('#')) {
    e.preventDefault();
    const targetElement = document.querySelector(href);
    if (targetElement) {
      // Dynamically get navbar height instead of hardcoding
      const navbar = document.querySelector('div[style*="position: sticky"]');
      const navbarHeight = navbar ? navbar.offsetHeight : 64;

      const elementPosition =
        targetElement.getBoundingClientRect().top + window.pageYOffset;
      const offsetPosition = elementPosition - navbarHeight;

      window.scrollTo({
        top: Math.max(0, offsetPosition),
        behavior: 'smooth'
      });
    }
  }

  // Close mobile menu when any link is clicked
  if (!isDesktop) {
    setIsMenuOpen(false);
    setIsShopOpen(false);
    setIsMusicOpen(false);
  }
};

  const getNavbarStyles = () => ({
    container: {
      position: 'sticky',
      top: 0,
      zIndex: 50,
      width: '100%',
      backgroundColor: 'white',
      borderBottom: '2px solid #f3f4f6',
      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
    },
    wrapper: {
      maxWidth: '1280px',
      margin: '0 auto',
      padding: isMobile ? '0 12px' : isTablet ? '0 16px' : '0 20px'
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      height: isMobile ? '56px' : isTablet ? '60px' : '64px'
    },
    brand: {
      fontSize: isMobile ? '22px' : isTablet ? '26px' : '28px',
      fontWeight: '900', // Changed from '1500' to '900' (ultra bold)
      color: '#e60000',
      letterSpacing: '1.5px', // Increased letter spacing for better visual impact
      textTransform: 'uppercase',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif', // Better font stack for bold weights
      cursor: 'pointer',
      textShadow: '0 1px 2px rgba(230, 0, 0, 0.1)', // Subtle shadow for depth
      transition: 'all 0.2s ease'
    },
    desktopMenu: {
      display: isDesktop ? 'flex' : 'none',
      alignItems: 'center',
      gap: '6px',
      position: 'relative'
    },
    dropdownMenu: {
      position: 'absolute',
      top: '100%',
      backgroundColor: 'white',
      border: '1px solid #e60000',
      borderRadius: '6px',
      boxShadow: '0 2px 10px rgba(255,165,0,0.2)',
      minWidth: '200px',
      zIndex: 1000
    },
    // Specific positioning for shop dropdown (align to left)
    shopDropdownMenu: {
      position: 'absolute',
      top: '100%',
      left: 0,
      backgroundColor: 'white',
      border: '1px solid #e60000',
      borderRadius: '6px',
      boxShadow: '0 2px 10px rgba(255,165,0,0.2)',
      minWidth: '200px',
      zIndex: 1000
    },
    // Specific positioning for music dropdown (align to right to prevent overflow)
    musicDropdownMenu: {
      position: 'absolute',
      top: '100%',
      right: 0,
      backgroundColor: 'white',
      border: '1px solid #e60000',
      borderRadius: '6px',
      boxShadow: '0 2px 10px rgba(255,165,0,0.2)',
      minWidth: '200px',
      zIndex: 1000
    },
    dropdownItem: {
      display: 'block',
      padding: '10px 14px',
      fontSize: '14px',
      color: '#333',
      textDecoration: 'none',
      borderBottom: '1px solid #f3f4f6'
    },
    mobileButton: {
      display: isDesktop ? 'none' : 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '10px',
      borderRadius: '50%',
      color: '#e60000',
      backgroundColor: 'transparent',
      border: '2px solid #e60000',
      cursor: 'pointer',
      transition: 'background 0.2s ease'
    },
    mobileMenu: {
      display: isDesktop ? 'none' : isMenuOpen ? 'block' : 'none',
      padding: '8px 0 12px 0',
      borderTop: '1px solid #f3f4f6'
    }
  });

  const getLinkStyle = (isMobileLink = false) => ({
    display: isMobileLink ? 'block' : 'inline-block',
    padding: isMobileLink ? '14px 12px' : '8px 12px',
    fontSize: isMobileLink ? '16px' : '14px',
    fontWeight: '600',
    color: '#333',
    textDecoration: 'none',
    borderRadius: '6px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    transition: 'all 0.2s ease'
  });

  const styles = getNavbarStyles();

  return (
    <div style={styles.container}>
      <div style={styles.wrapper}>
        <div style={styles.header}>
          <div 
            style={styles.brand}
            onMouseEnter={(e) => e.target.style.transform = 'scale(1.02)'}
            onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
          >
            HOTMESS
          </div>

          <div style={styles.desktopMenu}>
            {navItems.map((item, index) =>
              item.dropdown ? (
                <div
                  key={index}
                  style={{ position: 'relative' }}
                  onMouseEnter={() => item.musicDropdown ? setIsMusicOpen(true) : setIsShopOpen(true)}
                  onMouseLeave={() => item.musicDropdown ? setIsMusicOpen(false) : setIsShopOpen(false)}
                >
                  <button
                    style={{
                      ...getLinkStyle(),
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      color: '#e60000'
                    }}
                  >
                    {item.name} <ChevronDown size={16} />
                  </button>
                  {((item.musicDropdown && isMusicOpen) || (!item.musicDropdown && isShopOpen)) && (
                    <div style={item.musicDropdown ? styles.musicDropdownMenu : styles.shopDropdownMenu}>
                      {item.musicDropdown ? (
                        musicOptions.map((option, optIndex) => (
                          <a
                            key={optIndex}
                            href={option.href}
                            target={isExternalLink(option.href) ? '_blank' : '_self'}
                            rel={isExternalLink(option.href) ? 'noopener noreferrer' : undefined}
                            style={{
                              ...styles.dropdownItem,
                              borderBottom: optIndex === musicOptions.length - 1 ? 'none' : '1px solid #f3f4f6'
                            }}
                            onMouseEnter={(e) => (e.target.style.backgroundColor = '#fff8f0')}
                            onMouseLeave={(e) => (e.target.style.backgroundColor = 'white')}
                            onClick={(e) => handleLinkClick(option.href, e)}
                          >
                            {option.name}
                          </a>
                        ))
                      ) : (
                        categories.map((cat, catIndex) => (
                          <a
                            key={cat.id}
                            href={`https://${SHOPIFY_DOMAIN}/collections/${cat.handle}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              ...styles.dropdownItem,
                              borderBottom: catIndex === categories.length - 1 ? 'none' : '1px solid #f3f4f6'
                            }}
                            onMouseEnter={(e) => (e.target.style.backgroundColor = '#fff8f0')}
                            onMouseLeave={(e) => (e.target.style.backgroundColor = 'white')}
                          >
                            {cat.title}
                          </a>
                        ))
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <a
                  key={index}
                  href={item.href}
                  target={isExternalLink(item.href) ? '_blank' : '_self'}
                  rel={isExternalLink(item.href) ? 'noopener noreferrer' : undefined}
                  style={getLinkStyle()}
                  onMouseEnter={(e) => (e.target.style.color = '#e60000')}
                  onMouseLeave={(e) => (e.target.style.color = '#333')}
                >
                  {item.name}
                </a>
              )
            )}
          </div>

          <button
            style={styles.mobileButton}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <X size={isMobile ? 26 : 30} /> : <Menu size={isMobile ? 26 : 30} />}
          </button>
        </div>

        <div style={styles.mobileMenu}>
          {navItems.map((item, index) =>
            item.dropdown ? (
              <div key={index}>
                <button
                  style={{
                    ...getLinkStyle(true),
                    background: 'transparent',
                    border: 'none',
                    width: '100%',
                    textAlign: 'left',
                    color: '#e60000',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                  onClick={() => item.musicDropdown ? setIsMusicOpen(!isMusicOpen) : setIsShopOpen(!isShopOpen)}
                >
                  {item.name}
                  <ChevronDown 
                    size={16} 
                    style={{
                      transform: (item.musicDropdown ? isMusicOpen : isShopOpen) ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.2s ease'
                    }}
                  />
                </button>
                {((item.musicDropdown && isMusicOpen) || (!item.musicDropdown && isShopOpen)) && (
                  <div style={{ paddingLeft: '12px' }}>
                    {item.musicDropdown ? (
                      musicOptions.map((option, optIndex) => (
                        <a
                          key={optIndex}
                          href={option.href}
                          target={isExternalLink(option.href) ? '_blank' : '_self'}
                          rel={isExternalLink(option.href) ? 'noopener noreferrer' : undefined}
                          style={{ ...getLinkStyle(true), color: '#333' }}
                          onClick={(e) => handleLinkClick(option.href, e)}
                        >
                          {option.name}
                        </a>
                      ))
                    ) : (
                      categories.map((cat) => (
                        <a
                          key={cat.id}
                          href={`https://${SHOPIFY_DOMAIN}/collections/${cat.handle}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ ...getLinkStyle(true), color: '#333' }}
                          onClick={(e) => handleLinkClick(`https://${SHOPIFY_DOMAIN}/collections/${cat.handle}`, e)}
                        >
                          {cat.title}
                        </a>
                      ))
                    )}
                  </div>
                )}
              </div>
            ) : (
              <a
                key={index}
                href={item.href}
                target={isExternalLink(item.href) ? '_blank' : '_self'}
                rel={isExternalLink(item.href) ? 'noopener noreferrer' : undefined}
                style={getLinkStyle(true)}
                onClick={(e) => handleLinkClick(item.href, e)}
              >
                {item.name}
              </a>
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default Navbar;